import React, { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search, X, CornerDownLeft } from "lucide-react";
import { searchBook } from "../lib/api";

const Snippet = ({ text, query }) => {
  if (!query) return <>{text}</>;
  const lower = text.toLowerCase();
  const q = query.toLowerCase();
  const parts = [];
  let i = 0;
  let key = 0;
  while (i < text.length) {
    const idx = lower.indexOf(q, i);
    if (idx === -1) {
      parts.push(<span key={key++}>{text.slice(i)}</span>);
      break;
    }
    if (idx > i) parts.push(<span key={key++}>{text.slice(i, idx)}</span>);
    parts.push(
      <mark key={key++} className="rounded bg-gold/30 px-0.5 text-ink">
        {text.slice(idx, idx + q.length)}
      </mark>
    );
    i = idx + q.length;
  }
  return <>{parts}</>;
};

export const SearchInside = ({ open, onClose, onJump }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const inputRef = useRef(null);
  const debounce = useRef(null);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current && inputRef.current.focus(), 120);
    } else {
      setQuery("");
      setResults([]);
      setSearched(false);
    }
  }, [open]);

  const run = useCallback(async (q) => {
    const term = q.trim();
    if (term.length < 2) {
      setResults([]);
      setSearched(false);
      return;
    }
    setLoading(true);
    try {
      const data = await searchBook(term);
      setResults(data.results || []);
    } catch (e) {
      setResults([]);
    } finally {
      setLoading(false);
      setSearched(true);
    }
  }, []);

  const onChange = (v) => {
    setQuery(v);
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(() => run(v), 260);
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape" && open) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-ink/45 backdrop-blur-sm"
            onClick={onClose}
            data-testid="search-overlay"
          />
          <div className="pointer-events-none fixed inset-0 z-[61] flex items-center justify-center p-4 safe-top safe-bottom">
            <motion.div
              initial={{ opacity: 0, y: 8, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 8, scale: 0.98 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="pointer-events-auto flex max-h-[82vh] w-[min(94vw,620px)] flex-col overflow-hidden rounded-2xl bg-ivory-100 shadow-2xl"
              data-testid="search-panel"
            >
            <div className="flex items-center gap-3 border-b border-ivory-400/60 px-5 py-4">
              <Search strokeWidth={1.6} className="h-5 w-5 flex-none text-gold-deep" />
              <input
                ref={inputRef}
                value={query}
                onChange={(e) => onChange(e.target.value)}
                placeholder="Search inside the book…"
                data-testid="search-input"
                className="w-full bg-transparent font-display text-lg text-ink placeholder:text-ink-muted focus:outline-none"
              />
              <button
                type="button"
                onClick={onClose}
                aria-label="Close search"
                data-testid="search-close"
                className="rounded-full p-1.5 text-ink-soft hover:text-ink"
              >
                <X strokeWidth={1.5} className="h-5 w-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-2 py-2" data-testid="search-results">
              {loading && <p className="px-4 py-6 text-center text-sm text-ink-muted">Searching the trail…</p>}
              {!loading && searched && results.length === 0 && (
                <p className="px-4 py-6 text-center text-sm text-ink-muted" data-testid="search-empty">
                  No passages found for “{query.trim()}”.
                </p>
              )}
              {!loading && !searched && (
                <p className="px-4 py-6 text-center text-sm text-ink-muted">
                  Type at least two letters to find any passage.
                </p>
              )}
              {!loading &&
                results.map((r) => (
                  <button
                    key={r.page}
                    type="button"
                    onClick={() => onJump(r.page)}
                    data-testid={`search-result-${r.page}`}
                    className="group mb-1 flex w-full items-start gap-3 rounded-xl px-3 py-3 text-left transition hover:bg-gold/10"
                  >
                    <span className="mt-0.5 flex-none rounded-md bg-ivory-300 px-2 py-1 font-display text-xs text-gold-deep">
                      p. {r.page}
                    </span>
                    <span className="min-w-0 flex-1 text-sm leading-relaxed text-ink-soft">
                      <Snippet text={r.snippet} query={query.trim()} />
                    </span>
                    <CornerDownLeft
                      strokeWidth={1.5}
                      className="mt-1 h-4 w-4 flex-none text-ink-muted opacity-0 transition group-hover:opacity-100"
                    />
                  </button>
                ))}
            </div>

            {!loading && results.length > 0 && (
              <div className="border-t border-ivory-400/60 px-5 py-2.5 text-center text-xs text-ink-muted">
                {results.length} {results.length === 1 ? "passage" : "passages"} · tap one to jump there
              </div>
            )}
            </motion.div>
          </div>
        </>
      )}
    </AnimatePresence>
  );
};
